import type { SmartScheduleEvent } from '../smartSchedule/types';
import { toSmartScheduleKst, SmartScheduleDateInput } from '../smartSchedule/date';
import {
    QuickMoveSource,
    QuickMoveUndetermined,
    QuickMoveWarning,
    QuickMoveWarningCode,
    QuickMoveWarningResult,
} from './types';

/** 경고 표시 순서 — 돈·기록에 영향이 큰 것부터. */
const WARNING_ORDER: QuickMoveWarningCode[] = [
    'SETTLEMENT_MONTH_CHANGED',
    'PAST_ORIGINAL',
    'PAST_TARGET',
    'NOSHOW_KEPT',
    'MEMBERSHIP_OUT_OF_RANGE',
    'DUET_SPLIT',
    'POST_LINK',
    'CONDITION_UNLINK',
    'BUNDLE_LEAVE',
    'MEMBER_NOTIFICATION',
];

const CONFIRM_WARNING_MESSAGES: Partial<Record<QuickMoveWarningCode, string>> = {
    SETTLEMENT_MONTH_CHANGED: '정산 월이 바뀌어요',
    PAST_TARGET: '지난 시각으로 옮겨요',
    DUET_SPLIT: '이 회원만 옮겨져요(1:2)',
    POST_LINK: '수업일지 연결이 끊길 수 있어요',
    CONDITION_UNLINK: '컨디션 기록 연결이 풀려요',
    BUNDLE_LEAVE: '반복 묶음에서 빠져요',
};

/** 간단 확인 창에는 중요한 경고만 짧은 문구로 이어 표시한다. */
export const summarizeQuickMoveWarningsForConfirm = (source: Pick<QuickMoveSource, 'kind'>, warnings: QuickMoveWarning[]): string =>
    warnings.map((warning) => warning.code === 'PAST_ORIGINAL'
        ? source.kind === 'general' ? '지난 일정' : '지난 수업'
        : CONFIRM_WARNING_MESSAGES[warning.code]).filter(Boolean).join(' · ');

export interface ComputeQuickMoveWarningsInput {
    source: QuickMoveSource;
    /** validateQuickMoveDraft 가 돌려준 전송용 ISO. */
    target: { startTime: string; endTime: string };
    /** 테스트용. 기본값 현재 시각. */
    now?: SmartScheduleDateInput;
    /**
     * 같은 시각 다른 회원 PT(1:2 짝) 판정용 이벤트 목록 — 원래 날짜가 들어 있는 Smart 정규화 이벤트
     * (필터 적용 전). 넘기지 않으면 그 항목은 "판정 불가"로 남는다.
     */
    slotEvents?: SmartScheduleEvent[];
}

const hasValue = (value: unknown): boolean =>
    value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '');

const monthLabel = (value: SmartScheduleDateInput, withYear: boolean): string => {
    const date = toSmartScheduleKst(value);
    return withYear ? date.format('YYYY년 M월') : date.format('M월');
};

const formatDate = (value: unknown): string => toSmartScheduleKst(value as string).format('YYYY.MM.DD');

const parseOptionalDate = (value: unknown) => {
    if (!hasValue(value)) return null;
    try {
        const parsed = toSmartScheduleKst(value as string);
        return parsed.isValid() ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * 시트 경고 목록(§3 + §6-6 문구). 데이터가 rawItems 에 없는 항목은 `undetermined` 로 돌려준다.
 * 경고는 차단이 아니다(D2) — 공유 수강권 기간·정산 경계는 서버가 거절한다.
 */
export const computeQuickMoveWarnings = ({
    source,
    target,
    now = new Date(),
    slotEvents,
}: ComputeQuickMoveWarningsInput): QuickMoveWarningResult => {
    const warnings: QuickMoveWarning[] = [];
    const undetermined: QuickMoveUndetermined[] = [];
    const raw = source.raw || {};
    const isPrivate = source.kind === 'private';

    const nowKst = toSmartScheduleKst(now);
    const originalStart = toSmartScheduleKst(source.startTime);
    const originalEnd = toSmartScheduleKst(source.endTime);
    const targetStart = toSmartScheduleKst(target.startTime);
    const targetEnd = toSmartScheduleKst(target.endTime);
    const dateChanged = originalStart.format('YYYY-MM-DD') !== targetStart.format('YYYY-MM-DD');

    // 지난 수업 정정(운영 실측 24.5%) — 차단하지 않고 알린다.
    if (originalStart.isBefore(nowKst)) {
        warnings.push({
            code: 'PAST_ORIGINAL',
            tone: 'caution',
            message: isPrivate ? '이미 지난 수업이에요.' : '이미 지난 일정이에요.',
        });
    } else if (targetStart.isBefore(nowKst)) {
        warnings.push({ code: 'PAST_TARGET', tone: 'caution', message: '지난 시각으로 옮겨요.' });
    }

    if (isPrivate) {
        // 정산은 시작 월 기준. 월이 바뀌면 목적지 월 요율로 계산된다.
        if (originalStart.format('YYYY-MM') !== targetStart.format('YYYY-MM')) {
            const withYear = originalStart.year() !== targetStart.year();
            const isPastMonth = targetStart.format('YYYY-MM') < nowKst.format('YYYY-MM');
            warnings.push({
                code: 'SETTLEMENT_MONTH_CHANGED',
                tone: 'caution',
                message: `정산 월이 바뀌어요 (${monthLabel(originalStart, withYear)} → ${monthLabel(
                    targetStart,
                    withYear
                )})${isPastMonth ? ' · 이미 지난달이에요.' : ''}`,
            });
        }

        if (source.attendanceStatusRaw === 'NOSHOW') {
            warnings.push({
                code: 'NOSHOW_KEPT',
                tone: 'caution',
                message: '노쇼 수업이에요. 옮겨도 노쇼 상태는 유지돼요.',
            });
        }

        // 수강권 유효기간 — v4 범위 조회가 PT 행에 붙이는 membership(activationDate·expirationDate).
        const membership = raw.membership;
        if (!membership || typeof membership !== 'object') {
            undetermined.push({
                code: 'MEMBERSHIP_OUT_OF_RANGE',
                reason: '일정 응답에 membership 이 없어 수강권 기간을 알 수 없음',
            });
        } else {
            const activation = parseOptionalDate(membership.activationDate);
            const expiration = parseOptionalDate(membership.expirationDate);
            if (!activation && !expiration) {
                undetermined.push({
                    code: 'MEMBERSHIP_OUT_OF_RANGE',
                    reason: 'membership 에 activationDate·expirationDate 가 모두 없음',
                });
            } else {
                // 날짜 단위(KST) 비교 — 만료일 당일은 기간 안으로 본다.
                const targetDay = targetStart.format('YYYY-MM-DD');
                const beforeStart = activation ? targetDay < activation.format('YYYY-MM-DD') : false;
                const afterEnd = expiration ? targetDay > expiration.format('YYYY-MM-DD') : false;
                if (beforeStart || afterEnd) {
                    const range = `${activation ? formatDate(membership.activationDate) : ''} ~ ${
                        expiration ? formatDate(membership.expirationDate) : ''
                    }`.trim();
                    warnings.push({
                        code: 'MEMBERSHIP_OUT_OF_RANGE',
                        tone: 'caution',
                        message: `수강권 유효기간 밖이에요 (${range}).`,
                    });
                }
            }
        }

        // 같은 시각 다른 회원 PT(1:2 짝) — 함께 옮기기는 1차 제외(D5), 이 회원만 옮겨진다.
        if (!slotEvents) {
            undetermined.push({
                code: 'DUET_SPLIT',
                reason: '같은 시각 일정 목록(slotEvents)을 받지 못함',
            });
        } else {
            const partners = slotEvents.filter(
                (event) =>
                    event.kind === 'private' &&
                    event.id !== source.scheduleId &&
                    event.coachId === source.coachId &&
                    !event.memberIds.includes(source.memberId) &&
                    toSmartScheduleKst(event.startTime).valueOf() === originalStart.valueOf() &&
                    toSmartScheduleKst(event.endTime).valueOf() === originalEnd.valueOf()
            );
            if (partners.length > 0) {
                const stillOverlaps = targetStart.isBefore(originalEnd) && targetEnd.isAfter(originalStart);
                warnings.push({
                    code: 'DUET_SPLIT',
                    tone: 'caution',
                    message: stillOverlaps
                        ? '이 회원만 옮겨집니다(같은 시각 수업과 분리). 짝 수업과 시간이 계속 겹치면 강사 일정 중복으로 거절될 수 있어요.'
                        : '이 회원만 옮겨집니다(같은 시각 수업과 분리).',
                });
            }
        }

        if (hasValue(raw.postId)) {
            warnings.push({
                code: 'POST_LINK',
                tone: 'caution',
                message: '수업일지 연결이 자동으로 이어지지 않을 수 있어요.',
            });
        }

        if (hasValue(raw.conditionId) && dateChanged) {
            warnings.push({
                code: 'CONDITION_UNLINK',
                tone: 'caution',
                message: '다른 날로 옮기면 그날 컨디션 기록 연결이 풀려요.',
            });
        }

        if (hasValue(raw.scheduleBundleId)) {
            warnings.push({ code: 'BUNDLE_LEAVE', tone: 'caution', message: '반복 일정 묶음에서 빠져요.' });
        }

        // 기기 토큰 유무는 응답에 없다 — 조건부 문장으로만 안내한다(D7: 항상 발송).
        warnings.push({
            code: 'MEMBER_NOTIFICATION',
            tone: 'info',
            message: '기기 알림을 켠 회원에게 변경 알림이 가요.',
        });
    }

    warnings.sort((left, right) => WARNING_ORDER.indexOf(left.code) - WARNING_ORDER.indexOf(right.code));
    return { warnings, undetermined };
};
